// ###### IMPORTS
import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  DocumentIcon,
  TableCellsIcon,
  FunnelIcon,
} from '@heroicons/react/24/solid';
import { buildHierarchy, sortedLevel1Keys } from '../Detailed/CardAssetsDetailed';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import DropdownCustomDark from 'views/finance/moduleComponents/DropdownCustomDark';
import { formatDate } from 'shared_items/formatConventions';

// ###### HELPERS
const formatValue = (v) => {
  if (v === null || v === undefined || v === 0) return '—';
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const sumSnapshotValue = (assets, snapshotMap) =>
  assets.reduce((sum, a) => sum + (snapshotMap?.[a.id]?.snapshot_value ?? 0), 0);

const sumMarketValue = (assets, snapshotMap) =>
  assets.reduce((sum, a) => sum + (snapshotMap?.[a.id]?.market_value ?? snapshotMap?.[a.id]?.snapshot_value ?? 0), 0);

const getValueFromMap = (map, assetId, valueMode) => {
  if (!map || !assetId) return null;
  const entry = map[assetId];
  if (!entry) return null;
  if (valueMode === 'market') {
    const mv = entry.market_value;
    if (mv != null && mv !== 0) return mv;
    return entry.snapshot_value ?? null;
  }
  return entry.snapshot_value ?? null;
};

const sumByValueMode = (assets, snapshotMap, valueMode) =>
  valueMode === 'market' ? sumMarketValue(assets, snapshotMap) : sumSnapshotValue(assets, snapshotMap);

/** True if snapshot_value !== 0 in at least one map (same idea as Detailed card). */
const assetHasNonZeroSnapshotInAnyMap = (asset, mapsList) =>
  mapsList.some((mp) => (mp?.[asset.id]?.snapshot_value ?? 0) !== 0);

/** Fixed width for each numeric / snapshot column; first column takes the rest (table-fixed). */
const SNAPSHOT_VALUE_COL_CLASS = 'w-24 md:w-36';

const COL2_VALUE_MODE_SAME = 'same';
const COL2_VALUE_MODE_OPTIONS = [COL2_VALUE_MODE_SAME, 'base', 'market'];

const valueModeLabel = (mode) => (mode === 'market' ? 'market' : 'base');

const col2ValueModePickerLabel = (mode) =>
  mode === COL2_VALUE_MODE_SAME ? '(same)' : valueModeLabel(mode);

const resolveColumnValueMode = (colIndex, pageValueMode, col2Selection) => {
  if (colIndex === 0) return pageValueMode;
  if (col2Selection === COL2_VALUE_MODE_SAME) return pageValueMode;
  return col2Selection === 'market' ? 'market' : 'base';
};

const VALUE_MODE_LABEL_CLASS = 'text-xs font-normal text-gray-400';
const COLUMN_DATE_CLASS = 'text-xs font-medium text-gray-500 normal-case';

/** Discrete base/market picker — matches static column-1 label + chevron only. */
const SnapshotValueModePicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-0.5 ${VALUE_MODE_LABEL_CLASS} hover:text-gray-600 focus:outline-none`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Value mode"
      >
        <span>{col2ValueModePickerLabel(value)}</span>
        <ChevronDownIcon className="h-3 w-3 shrink-0 opacity-45" aria-hidden />
      </button>
      {open && (
        <div
          className="absolute left-1/2 top-full z-30 mt-1 min-w-[5rem] -translate-x-1/2 rounded-md border border-gray-200 bg-white py-1 shadow-lg"
          role="listbox"
        >
          {COL2_VALUE_MODE_OPTIONS.map((mode) => (
            <button
              key={mode}
              type="button"
              role="option"
              aria-selected={value === mode}
              onClick={() => {
                onChange(mode);
                setOpen(false);
              }}
              className={`block w-full px-3 py-1 text-center text-xs ${
                value === mode ? 'bg-green-50 text-green-600' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {col2ValueModePickerLabel(mode)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const StaticDateValueHeader = ({ dateLabel, valueMode }) => (
  <div className="flex flex-col items-center gap-0.5 normal-case">
    <span className={COLUMN_DATE_CLASS}>{dateLabel}</span>
    <span className={VALUE_MODE_LABEL_CLASS}>{valueModeLabel(valueMode)}</span>
  </div>
);

// ###### COMPONENT
const BoardTableSnapshots = ({
  assets = [],
  categories = [],
  snapshotList = [],
  selectedSnapshotId2 = null,
  onSelectSnapshot2,
  snapshotMaps = [],
  snapshotDates = [],
  loading = false,
  snapshotItemsLoading = false,
  valueMode = 'base',
}) => {
  const dropdown2Ref = useRef(null);
  const snapshotsFiltersRef = useRef(null);
  const [snapshotsFiltersOpen, setSnapshotsFiltersOpen] = useState(false);
  const [showOnlyInSnapshots, setShowOnlyInSnapshots] = useState(true);
  const [valueModeCol2, setValueModeCol2] = useState(COL2_VALUE_MODE_SAME);

  const valueModeForColumn = (colIndex) =>
    resolveColumnValueMode(colIndex, valueMode, valueModeCol2);

  useEffect(() => {
    if (!snapshotsFiltersOpen) return undefined;
    const onPointerDown = (e) => {
      if (snapshotsFiltersRef.current && !snapshotsFiltersRef.current.contains(e.target)) {
        setSnapshotsFiltersOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [snapshotsFiltersOpen]);

  const maps = snapshotMaps.length ? snapshotMaps : [{}];
  const dates = snapshotDates.length ? snapshotDates : [null];

  const assetsForTable = useMemo(() => {
    const m = snapshotMaps.length ? snapshotMaps : [{}];
    if (!showOnlyInSnapshots) return assets;
    return assets.filter((a) => assetHasNonZeroSnapshotInAnyMap(a, m));
  }, [assets, snapshotMaps, showOnlyInSnapshots]);

  const hierarchy = useMemo(() => buildHierarchy(assetsForTable, categories), [assetsForTable, categories]);
  const sortedLevel1 = useMemo(() => sortedLevel1Keys(hierarchy), [hierarchy]);

  const data = useMemo(() => {
    return sortedLevel1.map((l1) => {
      const l2Keys = Object.keys(hierarchy[l1] || {}).sort((a, b) => {
        if (a === 'Not Classified') return 1;
        if (b === 'Not Classified') return -1;
        return a.localeCompare(b);
      });
      const l2Arr = l2Keys.map((l2) => {
        const list = hierarchy[l1][l2] || [];
        const sums = maps.map((mp, colIndex) =>
          sumByValueMode(list, mp, valueModeForColumn(colIndex))
        );
        return { l2, assets: list, sums, count: list.length };
      }).sort((a, b) => (b.sums[0] || 0) - (a.sums[0] || 0));
      const sumsL1 = maps.map((_, i) => l2Arr.reduce((s, x) => s + (x.sums[i] || 0), 0));
      return { l1, l2Arr, sums: sumsL1, count: l2Arr.reduce((s, x) => s + x.count, 0) };
    });
  }, [hierarchy, sortedLevel1, maps, valueMode, valueModeCol2]);

  const columnTotals = useMemo(
    () =>
      maps.map((mp, colIndex) =>
        sumByValueMode(assetsForTable, mp, valueModeForColumn(colIndex))
      ),
    [assetsForTable, maps, valueMode, valueModeCol2]
  );

  const renderSnapshotsCardHeader = (leftExtra = null) => (
    <div className="mb-2 flex items-start justify-between gap-2">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <TableCellsIcon className="h-5 w-5 flex-shrink-0 text-gray-500" />
        <span className="text-md font-semibold text-gray-800">Snapshots</span>
        {leftExtra}
      </div>
      <div className="relative flex-shrink-0" ref={snapshotsFiltersRef}>
        <button
          type="button"
          onClick={() => setSnapshotsFiltersOpen((o) => !o)}
          className={`rounded-full p-1 transition-all flex-shrink-0 ${
            showOnlyInSnapshots
              ? 'bg-green-400 text-white'
              : snapshotsFiltersOpen
                ? 'bg-gray-100 text-gray-600'
                : 'text-gray-400 hover:bg-gray-50 hover:text-gray-500'
          }`}
          aria-label="Filters for snapshots table"
          aria-expanded={snapshotsFiltersOpen}
          aria-haspopup="true"
        >
          <FunnelIcon className="h-4 w-4" />
        </button>
        {snapshotsFiltersOpen && (
          <div
            className="absolute right-0 top-full z-30 mt-1 w-64 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left shadow-lg ring-1 ring-black/5"
            role="menu"
          >
            <p className="mb-2 border-b border-gray-100 pb-2 text-xs font-medium text-gray-600">
              Table scope
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                role="switch"
                aria-checked={showOnlyInSnapshots}
                aria-label={showOnlyInSnapshots ? 'Only in snapshot' : 'All assets'}
                title={
                  showOnlyInSnapshots
                    ? 'Show only assets with non-zero value in at least one selected snapshot'
                    : 'Show all assets'
                }
                onClick={() => setShowOnlyInSnapshots((v) => !v)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-0 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-white ${
                  showOnlyInSnapshots ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 shrink-0 transform rounded-full bg-white shadow ring-0 transition-transform ${
                    showOnlyInSnapshots ? 'ml-0.5 translate-x-4' : 'translate-x-0.5'
                  }`}
                  aria-hidden
                />
              </button>
              <span className="select-none text-xs text-gray-700">
                {showOnlyInSnapshots ? 'only in snapshot' : 'all assets'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const [expandedL1, setExpandedL1] = useState({});
  const [expandedL2, setExpandedL2] = useState({});
  const toggleL1 = (l1) => setExpandedL1((prev) => ({ ...prev, [l1]: !prev[l1] }));
  const toggleL2 = (l1, l2) => setExpandedL2((prev) => ({ ...prev, [`${l1}__${l2}`]: !prev[`${l1}__${l2}`] }));

  if (loading) {
    return (
      <div className="bg-white shadow rounded-xl p-3 min-h-64 relative overflow-hidden">
        {renderSnapshotsCardHeader()}
        <div>
          <table className="min-w-full">
            <tbody>
              {[...Array(6)].map((_, idx) => (
                <tr key={idx}>
                  <td className="py-2">
                    <div className="h-4 w-40 bg-gray-200 rounded animate-pulse mb-1" />
                  </td>
                  <td className="py-2">
                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse ml-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 rounded-xl">
          <SpinnerLoad />
        </div>
      </div>
    );
  }

  if (!assets.length) {
    return (
      <div className="bg-white shadow rounded-xl p-3 min-h-64">
        {renderSnapshotsCardHeader(
          dates[0] && (
            <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
              ref. {formatDate(dates[0], 'display')}
            </span>
          )
        )}
        <div className="flex h-48 items-center justify-center">
          <div className="text-center">
            <DocumentIcon className="w-16 h-16 mx-auto text-gray-400 mb-3" />
            <div className="text-gray-500 text-sm font-medium">No snapshots available.</div>
          </div>
        </div>
      </div>
    );
  }

  const nValueCols = snapshotList.length > 0 ? Math.max(2, maps.length) : maps.length;

  const selectedSnap2 = snapshotList.find((s) => s.id === selectedSnapshotId2);
  const snapshotOptions = [
    { value: '', label: '—' },
    ...snapshotList.map((s) => ({
      value: formatDate(s.snapshot_date, 'display'),
      label: formatDate(s.snapshot_date, 'display'),
    })),
  ];
  const selectedValue2 = selectedSnap2 ? formatDate(selectedSnap2.snapshot_date, 'display') : '';
  const handleSelect2 = (value) => {
    if (value === '') {
      onSelectSnapshot2?.(null);
      return;
    }
    const s = snapshotList.find((snap) => formatDate(snap.snapshot_date, 'display') === value);
    if (s) onSelectSnapshot2?.(s.id);
  };

  const handleSelectCol2ValueMode = (value) => {
    if (value === COL2_VALUE_MODE_SAME || value === 'market' || value === 'base') {
      setValueModeCol2(value);
    }
  };

  const renderColumnHeader = (colIndex) => {
    const isSecondSnapshotCol = colIndex === 1 && snapshotList.length > 0;

    if (isSecondSnapshotCol) {
      return (
        <div className="flex w-full min-w-0 flex-col items-center gap-0.5 normal-case">
          <div className="w-full min-w-0" ref={dropdown2Ref}>
            <DropdownCustomDark
              options={snapshotOptions}
              selectedValue={selectedValue2}
              onSelect={handleSelect2}
              placeholder="—"
              width="w-full min-w-0"
              allowLabelWrap
              usePortal
              portalRef={dropdown2Ref}
              showRadio
              triggerBgOpacity={95}
              dropdownClassMenu="bg-gray-50 shadow-lg ring-1 ring-gray-200 border border-gray-200"
              dropdownClassOption="cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-100 text-gray-700 flex items-center"
            />
          </div>
          <SnapshotValueModePicker value={valueModeCol2} onChange={handleSelectCol2ValueMode} />
        </div>
      );
    }

    return (
      <StaticDateValueHeader
        dateLabel={dates[colIndex] ? formatDate(dates[colIndex], 'display') : 'Value'}
        valueMode={valueModeForColumn(colIndex)}
      />
    );
  };

  return (
    <div className="relative bg-white shadow rounded-xl p-3 min-h-64">
      {snapshotItemsLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-white/70">
          <SpinnerLoad />
        </div>
      )}
      {renderSnapshotsCardHeader()}
      <div>
        {data.length === 0 ? (
          <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/80 px-3 text-center text-sm text-gray-500">
            No assets match this filter for the selected snapshots.
          </div>
        ) : (
        <table className="w-full min-w-0 table-fixed border-separate border-spacing-y-1">
          <colgroup>
            <col className="min-w-0" />
            {[...Array(nValueCols)].map((_, i) => (
              <col key={i} className={SNAPSHOT_VALUE_COL_CLASS} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="min-w-0 py-2 px-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 rounded-l">
                Asset
              </th>
              {[...Array(nValueCols)].map((_, i) => (
                <th
                  key={i}
                  className={`py-2 px-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500 ${i === nValueCols - 1 ? 'rounded-r' : ''}`}
                >
                  {renderColumnHeader(i)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <React.Fragment key={row.l1}>
                <tr
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleL1(row.l1)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleL1(row.l1);
                    }
                  }}
                  className="bg-gray-100 hover:bg-gray-200 transition cursor-pointer"
                >
                  <td className="min-w-0 flex items-center rounded-l py-1.5 px-2 text-sm font-semibold text-gray-800">
                    <span className="mr-2 flex-shrink-0 text-gray-400">
                      {expandedL1[row.l1] ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                    </span>
                    {row.l1}
                    <span className="ml-2 text-xs text-gray-400">({row.count})</span>
                  </td>
                  {[...Array(nValueCols)].map((_, i) => (
                    <td
                      key={i}
                      className={`py-1.5 px-2 text-right text-sm font-bold tabular-nums text-gray-800 ${i === nValueCols - 1 ? 'rounded-r' : ''}`}
                    >
                      {formatValue(row.sums[i])}
                    </td>
                  ))}
                </tr>
                {expandedL1[row.l1] &&
                  row.l2Arr.map((l2Row) => (
                    <React.Fragment key={l2Row.l2}>
                      <tr
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleL2(row.l1, l2Row.l2)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleL2(row.l1, l2Row.l2);
                          }
                        }}
                        className="bg-gray-50 hover:bg-gray-100 transition cursor-pointer"
                      >
                        <td className="min-w-0 flex items-center rounded-l py-1.5 px-2 pl-4 text-sm font-normal text-gray-700">
                          <span className="mr-2 flex-shrink-0 text-gray-400">
                            {expandedL2[`${row.l1}__${l2Row.l2}`] ? (
                              <ChevronDownIcon className="h-4 w-4" />
                            ) : (
                              <ChevronRightIcon className="h-4 w-4" />
                            )}
                          </span>
                          {l2Row.l2}
                          <span className="ml-2 text-xs text-gray-400">({l2Row.count})</span>
                        </td>
                        {[...Array(nValueCols)].map((_, i) => (
                          <td
                            key={i}
                            className={`py-1.5 px-2 text-right text-sm font-bold tabular-nums text-gray-800 ${i === nValueCols - 1 ? 'rounded-r' : ''}`}
                          >
                            {formatValue(l2Row.sums[i])}
                          </td>
                        ))}
                      </tr>
                      {expandedL2[`${row.l1}__${l2Row.l2}`] &&
                        l2Row.assets.map((asset) => (
                          <tr key={asset.id} className="bg-white hover:bg-gray-50 transition">
                            <td className="min-w-0 rounded-l py-2 px-2 pl-4 text-xs text-gray-600">
                              <div className="flex flex-col">
                                <span className="font-medium text-gray-700">{asset.asset_name || asset.asset_code || '—'}</span>
                                <span className="text-xs text-gray-400">{asset.asset_code || '—'}</span>
                              </div>
                            </td>
                            {[...Array(nValueCols)].map((_, i) => (
                              <td
                                key={i}
                                className={`py-2 px-2 text-right text-sm font-semibold tabular-nums text-gray-800 ${i === nValueCols - 1 ? 'rounded-r' : ''}`}
                              >
                                {formatValue(getValueFromMap(maps[i], asset.id, valueModeForColumn(i)))}
                              </td>
                            ))}
                          </tr>
                        ))}
                    </React.Fragment>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-200">
              <td className="rounded-l py-2 px-2 text-xs font-medium text-gray-500">Total</td>
              {[...Array(nValueCols)].map((_, i) => (
                <td
                  key={i}
                  className={`py-2 px-2 text-right text-xs font-semibold tabular-nums text-gray-600 ${i === nValueCols - 1 ? 'rounded-r' : ''}`}
                >
                  {formatValue(columnTotals[i])}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
        )}
      </div>
    </div>
  );
};

export default BoardTableSnapshots;
